import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { NAV_LINKS } from '../../data/site';
import { EASE } from './Reveal';

const LogoMark = () => (
  <svg width="34" height="34" viewBox="0 0 64 64" aria-hidden="true">
    <rect width="64" height="64" rx="14" fill="#234D36" />
    <path d="M32 50c0-12 6-20 18-24-2 14-8 22-18 24z" fill="#A8C3A0" />
    <path d="M32 50c0-10-5-17-15-20 1 11 6 18 15 20z" fill="#C89B3C" />
    <rect x="30.5" y="44" width="3" height="10" rx="1.5" fill="#F7F3E8" />
  </svg>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('home');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const sections = NAV_LINKS.map((l) => document.querySelector(l.href)).filter(Boolean);
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) setActive(en.target.id);
        });
      },
      { rootMargin: '-40% 0px -55% 0px' }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <motion.header
        data-testid="navbar"
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.2, ease: EASE }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
          scrolled
            ? 'bg-cream/85 backdrop-blur-md shadow-card border-b border-line/70'
            : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div
          className={`mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 transition-all duration-500 ${
            scrolled ? 'py-3' : 'py-5'
          }`}
        >
          <a href="#home" data-testid="nav-logo" className="flex items-center gap-3" aria-label="SKycodeX AgroTech home">
            <LogoMark />
            <span className="leading-none">
              <span className="block font-display text-lg font-bold tracking-tight text-ink">SKycodeX</span>
              <span className="block text-[10px] font-bold uppercase tracking-[0.32em] text-forest">AgroTech</span>
            </span>
          </a>

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`link-underline text-sm font-semibold tracking-wide transition-colors duration-300 hover:text-forest ${
                  active === link.href.slice(1) ? 'active text-forest' : 'text-ink/70'
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="#contact"
              data-testid="nav-enquiry-btn"
              className="group hidden items-center gap-2 rounded-full bg-forest px-5 py-2.5 text-sm font-semibold text-cream shadow-soft transition-all duration-300 hover:bg-forest-deep hover:shadow-lift sm:inline-flex"
            >
              Send Enquiry
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line bg-white/70 text-ink lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[70] flex flex-col bg-cream"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <span className="flex items-center gap-3">
                <LogoMark />
                <span className="font-display text-lg font-bold text-ink">SKycodeX AgroTech</span>
              </span>
              <button
                data-testid="nav-mobile-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line bg-white text-ink"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
              {NAV_LINKS.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.6, ease: EASE }}
                  className="border-b border-line/70 py-5 font-display text-3xl font-medium text-ink transition-colors hover:text-forest"
                >
                  {link.label}
                </motion.a>
              ))}
              <motion.a
                href="#contact"
                data-testid="mobile-nav-enquiry-btn"
                onClick={() => setOpen(false)}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6, ease: EASE }}
                className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-ink shadow-soft"
              >
                Send Enquiry <ArrowUpRight className="h-4 w-4" />
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
